import { FC, useEffect, useRef, useState } from "react";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { BeefInputBreedingIntervention, BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { useFormContext } from "react-hook-form";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import { BeefBaseline, BeefBreedingStageData } from "../../../../models/Baseline/BeefBaseline";
import BeefInputProductivityComponent from "./BeefInputProductivityComponent";
import { StageType } from "../../../../../../graphql/types";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import BeefInputStockComponent from "./BeefInputStockComponent";
import { Spacer } from "../../../helpers/Spacer";
import BeefInputBreedingNewAnimalsComponent from "./BeefInputBreedingNewAnimalsComponent";
import BeefInputIntSourceComponent from "./BeefInputIntSourceComponent";
import BeefInputExtSourceComponent from "./BeefInputExtSourceComponent";
import BeefInputBreedingCalvesComponent from "./BeefInputBreedingCalvesComponent";
import { cloneDeep } from "lodash";

interface BeefInputBreedingDialogProps {
  formType: FormType,
  stageIndex: number,
  baseline: BeefBaseline,
  productionProcessName: string,
  handleCancel: (param?: string | null) => void;
}

const BeefInputBreedingDialog: FC<BeefInputBreedingDialogProps> = ({
  stageIndex,
  formType,
  baseline,
  productionProcessName,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fieldItemPrefix = `stages.${stageIndex}.stageData.input`;

  const currResetValue = useRef<BeefInputBreedingIntervention>();
  const fc = useFormContext<BeefIntervention>();

  const currentStageData = baseline.stages[stageIndex].stageData;
  const beefInputData = (currentStageData as BeefBreedingStageData).input;

  const [isProductivityEdit, setIsProductivityEdit] = useState<boolean>(false);
  const [isStockEdit, setIsStockEdit] = useState<boolean>(false);
  const [isNewAnimalsEdit, setIsNewAnimalsEdit] = useState<boolean>(false);
  const [isIntEdit, setIsIntEdit] = useState<boolean>(false);
  const [isExtEdit, setIsExtEdit] = useState<boolean>(false);
  const [isCalvesEdit, setIsCalvesEdit] = useState<boolean>(false);

  const isInputEdit =
    isProductivityEdit ||
    isStockEdit ||
    isNewAnimalsEdit ||
    isIntEdit ||
    isExtEdit ||
    isCalvesEdit;

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  }, []);

  const productivityCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.cattleProductivity === undefined)
      fc.setValue(`${fieldItemPrefix}.cattleProductivity`, "");

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.cattleProductivity`, currResetValue.current?.cattleProductivity);

    setIsProductivityEdit(false);
  };

  const stockCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.animalsPresentAtStart === undefined) {
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtStart`, "");
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtEnd`, "");
      fc.setValue(`${fieldItemPrefix}.averageWeightAtStart`, "");
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtStart`, currResetValue.current?.animalsPresentAtStart);
      fc.setValue(`${fieldItemPrefix}.animalsPresentAtEnd`, currResetValue.current?.animalsPresentAtEnd);
      fc.setValue(`${fieldItemPrefix}.averageWeightAtStart`, currResetValue.current?.averageWeightAtStart);
    }

    setIsStockEdit(false);
  };

  const newAnimalsCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.averageWeightNewAnimals === undefined)
      fc.setValue(`${fieldItemPrefix}.averageWeightNewAnimals`, "");

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.averageWeightNewAnimals`, currResetValue.current?.averageWeightNewAnimals);

    setIsNewAnimalsEdit(false);
  };

  const intSourceCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.internalSources?.at(0)?.numberAnimals === undefined) {
      (beefInputData?.internalSources || []).forEach((intSource, index) => {
        const fieldPrefix = `${fieldItemPrefix}.internalSources[${index}]`;
        Object.keys(intSource).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "")
        })
      })
    }

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.internalSources`, currResetValue.current?.internalSources);

    setIsIntEdit(false);
  };

  const extSourceCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.externalSources?.at(0)?.numberAnimals === undefined) {
      (beefInputData?.externalSources || []).forEach((intSource, index) => {
        const fieldPrefix = `${fieldItemPrefix}.externalSources[${index}]`;
        Object.keys(intSource).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "")
        })
      })
    }

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.externalSources`, currResetValue.current?.externalSources);

    setIsExtEdit(false);
  };

  const calvesCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.averageWeightAtBirth === undefined) {
      fc.setValue(`${fieldItemPrefix}.averageWeightAtBirth`, "");
      fc.setValue(`${fieldItemPrefix}.permanencePeriod`, "");
    }
    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.averageWeightAtBirth`, currResetValue.current?.averageWeightAtBirth);
      fc.setValue(`${fieldItemPrefix}.permanencePeriod`, currResetValue.current?.permanencePeriod);
    }

    setIsCalvesEdit(false);
  };

  const handleDialogCancelClick = () => {
    productivityCancelHandler(false);
    stockCancelHandler(false);
    newAnimalsCancelHandler(false);
    intSourceCancelHandler(false);
    extSourceCancelHandler(false);
    calvesCancelHandler(false);

    fc.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel) {
      handleCancel();
    }
  };

  return (
    <DialogContainer
      formVisible
      handleClose={handleDialogCancelClick}
      iconCode="general/check-heart"
      formTitle={intl.formatMessage({ id: "SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE" })}
      datasetType="intervention"
      width={isInputEdit ? "1068px" : "800px"}
    >
      <BeefInputProductivityComponent
        formType={formType}
        stageType={StageType.Breeding}
        fieldItemPrefix={fieldItemPrefix}
        defaultStageData={beefInputData}
        isBatchGrowingOperation={false}
        isProductivityEdit={isProductivityEdit}
        setIsProductivityEdit={setIsProductivityEdit}
        productivityCancelHandler={productivityCancelHandler}
      />

      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.COWS' })}
      </p>

      <DsmGrid className={classes.dsmGridOneColumnInterventionWithBorder}>
        <BeefInputStockComponent
          formType={formType}
          stageType={StageType.Breeding}
          fieldItemPrefix={fieldItemPrefix}
          defaultStageData={beefInputData}
          isStockEdit={isStockEdit}
          setIsStockEdit={setIsStockEdit}
          stockCancelHandler={stockCancelHandler}
        />
      </DsmGrid>

      <Spacer height="24px" />

      <DsmGrid className={classes.dsmGridOneColumnWithBorder}>

        <BeefInputBreedingNewAnimalsComponent
          formType={formType}
          fieldItemPrefix={fieldItemPrefix}
          defaultStageData={beefInputData}
          isNewAnimalsEdit={isNewAnimalsEdit}
          setIsNewAnimalsEdit={setIsNewAnimalsEdit}
          newAnimalsCancelHandler={newAnimalsCancelHandler}
        />

        <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
          <div>
            <label
              htmlFor="internalSources"
              className={classes.headingTitleVariant}
            >
              {intl.formatMessage({
                id: "SUSTELL.STAGE.BEEF.INPUT.INTERNAL_SOURCE",
              })}
            </label>
            <div>
              <BeefInputIntSourceComponent
                formType={formType}
                stageType={StageType.Growing}
                fieldItemPrefix={fieldItemPrefix}
                productionProcessName={productionProcessName}
                defaultStageData={beefInputData}
                isInternalEdit={isIntEdit}
                setIsInternalEdit={setIsIntEdit}
                intSourceCancelHandler={intSourceCancelHandler}
                baseline={baseline}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="externalSources"
              className={classes.headingTitleVariant}
            >
              {intl.formatMessage({
                id: "SUSTELL.STAGE.BEEF.INPUT.EXTERNAL_SOURCE",
              })}
            </label>
            <div>
              <BeefInputExtSourceComponent
                formType={formType}
                stageType={StageType.Growing}
                fieldItemPrefix={fieldItemPrefix}
                defaultStageData={beefInputData}
                isExternalEdit={isExtEdit}
                setIsExternalEdit={setIsExtEdit}
                extSourceCancelHandler={extSourceCancelHandler}
              />
            </div>
          </div>
        </DsmGrid>
      </DsmGrid>

      <Spacer height="2px" />

      <BeefInputBreedingCalvesComponent
        formType={formType}
        fieldItemPrefix={fieldItemPrefix}
        defaultStageData={beefInputData}
        isCalvesEdit={isCalvesEdit}
        setIsCalvesEdit={setIsCalvesEdit}
        calvesCancelHandler={calvesCancelHandler}
      />

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  )
}

export default BeefInputBreedingDialog;
